import UIUtils from 'js/lib/UIUtils';
import { get, mapKeys } from 'lodash';
import moment from 'moment';
import React from 'react';
import classNames from 'classnames';
import DatePicker from 'react-widgets/DatePicker';
import { CircularProgress, Stack } from '@mui/material';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import {
  FieldBuilderRedesigned,
  HeaderField,
} from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { TextField } from 'new/elements/TextField';
import { ReactComponent as ApacheKafka } from 'new/components/icons/apache_kafka_symbol.svg';
import { formatCode } from 'js/lib/FormatUtils';
import { Button } from 'new/elements/Button';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from 'js/components/elements/editors/monaco/MonacoEditorUtils';
import DebouncedText from 'js/components/elements/editors/mui/DebouncedText';
import { getCurrentSelectedOption } from '../../elements/rselect/ReactSelectUtils';
import KafkaTopicsListContainer from './KafkaTopicsListContainer';

/**
 * Styles : CSS
 */
import './ConfigureAPI.scss';
import '../../elements/DateTimeField.scss';
import styles from './ConfigureKafka.module.scss';
import DataSourceDetails from 'js/components/data_sources/add_ds_form/DataSourceDetails/DataSourceDetails';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import JSONUtils from 'js/lib/JSONUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { UiMode } from 'new/constants/uiMode';
import { withUiModeCode, withUiModeDefault } from 'new/utils/uiMode';

moment.locale('en');

class ConfigureKafka extends React.Component {
  constructor(props) {
    super(props);

    this._getSelectField = this._getSelectField.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._handleChangeStartDate = this._handleChangeStartDate.bind(this);
    this._handleFetchSamplesClicked = this._handleFetchSamplesClicked.bind(this);
    this._handleSourceConfigChanged = this._handleSourceConfigChanged.bind(this);
    this._getTopicFieldName = this._getTopicFieldName.bind(this);
  }

  getResourceConfig = () => {
    const { source, sourceConfig, name, description } = this.props;
    return {
      name: source?.name || name,
      description: source?.description || description,
      source_config: sourceConfig,
    };
  };

  onSaveAdvancedMode = async (configString) => {
    const {
      onError = () => {},
      nextFormStage = () => {},
      onSaveAdvancedMode = () => {},
    } = this.props;

    if (!JSONUtils.isJSON(configString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const { name, description, source_config } = JSON.parse(configString);

    const data = {
      ...source_config,
      name: name || '',
      description: description || '',
    };

    const dataWithMode = withUiModeCode(data);
    const { error } = await tryCatchAsync(onSaveAdvancedMode(dataWithMode));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  validateRequiredFields = (config) => {
    const {
      paging: { sourceConfigById },
      selectedConnection,
    } = this.props;
    const fields = sourceConfigById[selectedConnection];
    const missingFields = getEmptyRequiredFields(fields, config);
    return !missingFields.length;
  };

  onSwitchUiMode = () => {
    const { sourceConfig, onSourceConfigChanged } = this.props;
    if (typeof onSourceConfigChanged !== 'function') return;

    onSourceConfigChanged({ ...sourceConfig, 'ui.mode': UiMode.VIEW });
  };

  render() {
    const {
      paging: { metaInfoById, sourceConfigById },
      sourceConfig,
      topics,
      queues,
      samples,
      selectedConnection,
      selectedCredentialId,
      isLoadingSample,
    } = this.props;

    const meta = metaInfoById[selectedConnection];
    const fields = sourceConfigById[selectedConnection];
    const fieldsByName = mapKeys(fields, 'name');
    const connectionName = meta ? meta.displayName : 'Kafka';
    const topicFieldName = this._getTopicFieldName();
    const topic = get(sourceConfig, topicFieldName);
    const offsetMode = get(sourceConfig, 'offset.mode');
    const fromDateString = get(sourceConfig, 'start.from.date');
    const fromDate = fromDateString ? moment(fromDateString).toDate() : null;
    const parserType = get(sourceConfig, 'parser.type');
    const topicSamples = get(samples, topic);

    let error;
    let response;
    let contentType;

    if (topicSamples) {
      error = topicSamples.error;
      contentType = topicSamples.contentType;
      response = formatCode(topicSamples.response, contentType);
    } else {
      error = null;
      response = '';
    }

    const responseFailure = error;
    const editorLanguage = getEditorLanguage(contentType);

    const inputTopicOrQueue = fieldsByName['source.type']
      ? {
          item: {
            display_name: 'Select whether source is a topic or queue',
            description: fieldsByName['source.type'].description,
          },
          input: this._getSelectField(fieldsByName['source.type']),
        }
      : {};

    // Determine whether to use queues or topics based on connector configuration
    const topicsToUse = this._computeTopicsToUse();

    const sourceEndpointNoun = topicFieldName === 'subscription' ? 'subscription' : 'topic';
    const inputTopicsList = {
      item: {
        display_name: fieldsByName['source.type']
          ? `Select ${connectionName} ${sourceEndpointNoun} or queue to read from`
          : `Select ${connectionName} ${sourceEndpointNoun} to read from`,
        description: fieldsByName[topicFieldName]?.description,
      },
      input: (
        <div
          className={classNames(
            styles.base_style_container,
            topicsToUse?.length ? styles.border_none : undefined,
          )}
        >
          <KafkaTopicsListContainer
            topicFieldName={topicFieldName}
            topics={topicsToUse}
            selectedTopic={topic}
            selectedCredentialId={selectedCredentialId}
            sourceType={get(sourceConfig, 'source.type', 'topic')}
            onTopicChanged={(topic) => this._handleSourceConfigChanged(topicFieldName, topic)}
          />
        </div>
      ),
    };

    const inputOffsetMode = fieldsByName['offset.mode']
      ? {
          item: {
            display_name: 'Select an offset mode',
            description: fieldsByName['offset.mode'].description,
          },
          input: this._getSelectField(fieldsByName['offset.mode']),
        }
      : {};

    const inputFromDate =
      offsetMode === 'from_date'
        ? {
            item: {
              display_name: 'Consume data from',
              description: fieldsByName['start.from.date'].description,
            },
            input: (
              <div className={classNames(styles.base_style_container, styles.padding_12)}>
                <DatePicker
                  value={fromDate}
                  valueFormat={{ dateStyle: 'long', timeStyle: 'short' }}
                  includeTime={false}
                  onChange={this._handleChangeStartDate}
                />
              </div>
            ),
          }
        : {};

    const inputOffsetModeManual =
      offsetMode === 'manual'
        ? {
            item: {
              display_name: 'Consume data from',
              description: fieldsByName['start.from.offsets'].description,
            },
            input: this._getTextField('start.from.offsets'),
          }
        : null;

    const inputSelectDataFormat = {
      item: {
        display_name: 'Select data format',
        description: fieldsByName['parser.type'].description,
      },
      input: this._getSelectField(fieldsByName['parser.type']),
    };

    const inputSerializationVariant =
      parserType === 'avro' && fieldsByName['serialization.variant']
        ? {
            item: {
              display_name: 'Serialization Variant',
              description: fieldsByName['serialization.variant'].description,
            },
            input: this._getSelectField(fieldsByName['serialization.variant']),
          }
        : {};

    const list = [
      inputTopicOrQueue,
      inputTopicsList,
      inputOffsetMode,
      inputFromDate,
      inputOffsetModeManual,
      inputSelectDataFormat,
      inputSerializationVariant,
    ].filter((i) => i?.item);

    const { name = '', description = '' } = this.props;
    const resourceConfig = this.getResourceConfig();

    return (
      <div className={styles.container}>
        <DataSourceDetails
          name={name}
          description={description}
          onInputChange={this.props.onMetaInfoChanged}
          leftView={
            <HeaderMetaConfiguration
              enableInputFields={false}
              connection={selectedConnection}
              isDestination={false}
              sourceOrDestinationChipChildren={
                <ResourcePopoverMenu
                  resourceConfig={resourceConfig}
                  connectorType={selectedConnection}
                  jsonConfigOnSave={this.onSaveAdvancedMode}
                  onSwitchUiMode={this.onSwitchUiMode}
                  resourcePersisted={!!this.props.sourceBeingEdited?.id || !!this.props.source?.id}
                  jsonConfigOnClose={() => {
                    if (this.props.goBack) {
                      this.props.goBack();
                    }
                  }}
                />
              }
            />
          }
        />

        <Stack padding={2} className={styles.inner_container}>
          <div className={classNames(styles.side)}>
            <section className="configuration-holder">
              <ol className={styles.input_list}>
                {list.map((element, index) => {
                  return (
                    <li key={element.item.display_name}>
                      <div className={styles.input_row}>
                        <div className={styles.input_header}>
                          <div className={styles.number_line}>
                            <span className={styles.text}>{index + 1}</span>
                          </div>

                          <HeaderField item={element.item} className={styles.input_label} />
                        </div>

                        {element.input}
                      </div>
                    </li>
                  );
                })}
              </ol>

              <div className={styles.to_fetch_container}>
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={!topic}
                  onClick={this._handleFetchSamplesClicked}
                >
                  {isLoadingSample ? <CircularProgress size={20} /> : 'Fetch Samples'}
                </Button>
              </div>
            </section>
          </div>

          <div className={classNames(styles.side, styles.base_style_container, styles.monaco_view)}>
            <h1 className={classNames(styles.samples_kafka_title, styles.padding)}>
              Samples From {connectionName}
              {responseFailure ? (
                <span className={styles.errorMessage}>{`(Failure: ${error})`}</span>
              ) : null}
            </h1>

            <LoadableMonacoEditor
              language={editorLanguage}
              theme="dark"
              value={response || ''}
              width="100%"
              height="100%"
              options={{
                readOnly: true,
                minimap: { enabled: false },
              }}
            />
          </div>
        </Stack>
      </div>
    );
  }

  _getSelectField(field, sort = false) {
    const { name } = field;
    const { sourceConfig } = this.props;
    const listOfClass = classNames(
      styles.base_style_container,
      styles.padding_12,
      styles.padding_bottom_20,
    );

    return (
      <div className={listOfClass}>
        <FieldBuilderRedesigned
          id={name}
          field={field}
          isRequired={field.required}
          showRequiredIndicator
          currentFieldValue={get(sourceConfig, name)}
          isClearable={!sort}
          backspaceRemovesValue={!sort}
          isSearchable={sort}
          onChange={(name, currValue) => {
            this._handleSourceConfigChanged(name, currValue);
          }}
        />
      </div>
    );
  }

  _getTextField(name) {
    const { sourceConfig } = this.props;
    const value = get(sourceConfig, name, '');

    return (
      <div className={classNames(styles.base_style_container, styles.padding_12)}>
        <DebouncedText
          id={name}
          value={value}
          rootStyle={{ height: 38, borderRadius: 2 }}
          style={{ padding: '2px 8px' }}
          onChange={(newValue) => this._handleSourceConfigChanged(name, newValue)}
        />
      </div>
    );
  }

  _handleChangeStartDate(date) {
    this._handleSourceConfigChanged('start.from.date', date && date.toISOString());
  }

  _getTopicFieldName() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
    } = this.props;
    const fields = sourceConfigById[selectedConnection];
    const fieldsByName = mapKeys(fields, 'name');
    let topicFieldName = 'topic';
    if (fieldsByName['source.name']) topicFieldName = 'source.name';
    else if (fieldsByName['subscription']) topicFieldName = 'subscription';
    return topicFieldName;
  }

  _computeTopicsToUse() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
      topics,
      subscriptions,
      queues,
      sourceConfig,
    } = this.props;

    // Pub/Sub source reads from a subscription, so use the subscriptions list
    // when the connector's topic-field name resolves to 'subscription'.
    if (this._getTopicFieldName() === 'subscription') {
      return subscriptions || [];
    }

    const fields = sourceConfigById[selectedConnection];
    const fieldsByName = mapKeys(fields, 'name');
    const hasTargetTypeField = fieldsByName['source.type'];
    const currentSourceType = get(sourceConfig, 'source.type', 'topic');
    const isQueueType = hasTargetTypeField && currentSourceType === 'queue';

    return isQueueType ? queues || [] : topics || [];
  }

  _handleFetchSamplesClicked() {
    const { sourceConfig, selectedCredentialId, probeGetKafkaTopicSample } = this.props;
    const topicFieldName = this._getTopicFieldName();

    probeGetKafkaTopicSample(
      selectedCredentialId,
      sourceConfig[topicFieldName],
      topicFieldName,
      sourceConfig,
    );
  }

  _handleSourceConfigChanged(name, value) {
    const {
      paging: { sourceConfigById },
      formType,
      sourceConfig,
      selectedConnection,
      updateFormData,
    } = this.props;
    const fields = sourceConfigById[selectedConnection];

    let updatedConfig = {
      ...sourceConfig,
      [name]: value,
    };

    // If source.type changed, clear the selected topic/queue since it's no longer valid
    if (name === 'source.type') {
      const topicFieldName = this._getTopicFieldName();
      updatedConfig[topicFieldName] = null;
    }

    const newSourceConfig = UIUtils.configWithMissingDefaults(updatedConfig, fields);
    const sourceConfigWithMode = withUiModeDefault(newSourceConfig);
    const formDataUpdate = { sourceConfig: sourceConfigWithMode };

    updateFormData(formType, formDataUpdate);
  }
}

export default ConfigureKafka;
